// Phase 2.5 Slice F — client-side WebSocket subscription to the RealtimeHub.
//
// One singleton connection per page. Components subscribe via useRealtime();
// when the page is fully unmounted (all subscribers gone) the connection is
// torn down. Reconnects with exponential backoff on close.
//
// Auth: Clerk Bearer token in the URL query (?token=…) because browsers can't
// set Authorization on a WebSocket handshake. The Worker reads it via
// withClerk middleware which accepts ?token= alongside Authorization.

import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import type { ClusterNode, McpApproval } from '../../shared/types';

export type RealtimeEvent =
  | { kind: 'hello'; t: number }
  | { kind: 'pong';  t: number }
  | { kind: 'approval_added';    approval: McpApproval }
  | { kind: 'approval_resolved'; id: string; status: McpApproval['status']; resolved_at: number }
  | { kind: 'cluster_node_ready'; node: ClusterNode }
  | { kind: 'cluster_node_lost';  id: string }
  | { kind: 'agent_run_pending';  run_id: string; agent_id: string; agent_name: string; created_at: number; trigger_type: 'scheduled' | 'webhook' | 'manual' };

type Listener = (e: RealtimeEvent) => void;

interface RealtimeChannel {
  ws: WebSocket | null;
  listeners: Set<Listener>;
  /** Reconnect attempt counter for exponential backoff. */
  attempt: number;
  /** True once close() was called intentionally. */
  shuttingDown: boolean;
  /** AbortController timer for the scheduled reconnect, if any. */
  reconnectHandle: ReturnType<typeof setTimeout> | null;
  /** Heartbeat ping interval handle. */
  heartbeatHandle: ReturnType<typeof setInterval> | null;
}

let channel: RealtimeChannel | null = null;

function ensureChannel(getToken: () => Promise<string | null>): RealtimeChannel {
  if (channel) return channel;
  channel = {
    ws: null,
    listeners: new Set(),
    attempt: 0,
    shuttingDown: false,
    reconnectHandle: null,
    heartbeatHandle: null,
  };
  void connect(channel, getToken);
  return channel;
}

async function connect(ch: RealtimeChannel, getToken: () => Promise<string | null>): Promise<void> {
  if (ch.shuttingDown) return;
  if (ch.ws && (ch.ws.readyState === WebSocket.OPEN || ch.ws.readyState === WebSocket.CONNECTING)) return;
  let token: string | null;
  try {
    token = await getToken();
  } catch {
    token = null;
  }
  // Build the WS URL relative to the current origin.
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${location.host}/api/realtime/connect${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect(ch, getToken);
    return;
  }
  ch.ws = ws;

  ws.onopen = () => {
    ch.attempt = 0;
    if (ch.heartbeatHandle) clearInterval(ch.heartbeatHandle);
    // Send a ping every 45 s so DO hibernation doesn't drop us; the runtime
    // typically reaps idle sockets after a few minutes.
    ch.heartbeatHandle = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ kind: 'ping' })); } catch { /* socket dying */ }
      }
    }, 45_000);
  };

  ws.onmessage = (ev) => {
    try {
      const parsed = JSON.parse(ev.data) as RealtimeEvent;
      for (const listener of ch.listeners) {
        try { listener(parsed); } catch { /* swallow — one listener can't break the rest */ }
      }
    } catch {
      // Non-JSON frame; ignore.
    }
  };

  ws.onclose = () => {
    if (ch.heartbeatHandle) {
      clearInterval(ch.heartbeatHandle);
      ch.heartbeatHandle = null;
    }
    ch.ws = null;
    scheduleReconnect(ch, getToken);
  };

  ws.onerror = () => {
    // Don't tear down — onclose will fire next.
  };
}

function scheduleReconnect(ch: RealtimeChannel, getToken: () => Promise<string | null>): void {
  if (ch.shuttingDown) return;
  if (ch.reconnectHandle) return;
  ch.attempt++;
  // Backoff: 500 ms, 1 s, 2 s, 4 s, …, capped at 30 s.
  const delay = Math.min(30_000, 500 * Math.pow(2, Math.min(ch.attempt - 1, 6)));
  ch.reconnectHandle = setTimeout(() => {
    ch.reconnectHandle = null;
    void connect(ch, getToken);
  }, delay);
}

function shutdownIfIdle(ch: RealtimeChannel): void {
  if (ch.listeners.size > 0) return;
  ch.shuttingDown = true;
  if (ch.reconnectHandle) clearTimeout(ch.reconnectHandle);
  if (ch.heartbeatHandle) clearInterval(ch.heartbeatHandle);
  if (ch.ws) {
    try { ch.ws.close(1000, 'idle'); } catch { /* already closed */ }
  }
  channel = null;
}

/**
 * Subscribe to RealtimeHub events. The listener fires on every event regardless
 * of kind — narrow with `event.kind` in the callback. Auto-cleanup on unmount.
 *
 * Each component just calls this with its own filter; the singleton ws is
 * shared across the page.
 */
export function useRealtime(listener: Listener, deps: unknown[] = []): void {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const stableListener = useRef(listener);
  stableListener.current = listener;

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const ch = ensureChannel(() => getToken());
    const wrapped: Listener = (e) => stableListener.current(e);
    ch.listeners.add(wrapped);
    return () => {
      ch.listeners.delete(wrapped);
      // Tear down on the next tick so HMR / fast strict-mode remounts don't
      // immediately re-open the socket.
      setTimeout(() => {
        if (channel === ch) shutdownIfIdle(ch);
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, ...deps]);
}
